/**
 * Formats recipe instructions from various input formats into a consistent string format.
 * 
 * @param {Object|Array|string} instructions - The instructions to format
 * @returns {string} Formatted instructions string
 */
export const formatInstructions = (instructions) => {
  // Handle empty or invalid input
  if (!instructions) return '';
  
  // If it's already a string, return as is
  if (typeof instructions === 'string') return instructions;
  
  // If it's an array, join with newlines
  if (Array.isArray(instructions)) {
    return instructions.join('\n');
  }
  
  // If it's an object, format each key-value pair
  if (typeof instructions === 'object') {
    return Object.entries(instructions)
      .map(([section, steps]) => {
        // Handle both string and array values
        const stepList = Array.isArray(steps) 
          ? steps.map(step => `- ${step}`).join('\n')
          : `- ${steps}`;
        
        return `${section}:\n${stepList}`;
      })
      .join('\n\n'); // Double newline between sections
  }
  
  // Fallback for any other type
  return String(instructions);
};

/**
 * Parses a formatted instructions string back into an object structure
 * 
 * @param {string} text - The formatted instructions text
 * @returns {Object} Parsed instructions object
 */
export const parseInstructions = (text) => {
  if (!text || typeof text !== 'string') return {};
  
  const sections = text.split(/\n\s*\n/);
  const result = {};
  
  for (const section of sections) {
    const lines = section.split('\n').filter(Boolean);
    if (lines.length === 0) continue;
    
    const sectionName = lines[0].replace(':', '').trim();
    const steps = lines.slice(1)
      .map(line => line.replace(/^\s*-\s*/, '').trim())
      .filter(step => step);
    
    if (sectionName) {
      result[sectionName] = steps.length ? steps : '';
    }
  }
  
  return result;
};
